
import { useEffect, useRef, useState } from "react";
import type { ChartItem } from "@/services/api";
import { useTheme } from "@/context/ThemeContext";
import "./PlotlyChart.css";

interface Props {
  chart: ChartItem;
}

const THEME_COLORS = {
  dark: {
    font: "#94a3b8",
    title: "#cbd5e1",
    grid: "#1e293b",
    legend: "#64748b",
    label: "#cbd5e1",
  },
  light: {
    font: "#475569",
    title: "#334155",
    grid: "#e2e8f0",
    legend: "#64748b",
    label: "#334155",
  },
};

export function PlotlyChart({ chart }: Props) {
  const divRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const { theme } = useTheme();

  useEffect(() => {
    if (!divRef.current) return;

    let cancelled = false;
    const colors = THEME_COLORS[theme];

    import("plotly.js-dist-min").then((Plotly) => {
      if (cancelled || !divRef.current) return;
      try {
        const fig = JSON.parse(chart.plotly_json);
        const rawTitle = fig.layout?.title;
        const hasInternalTitle = typeof rawTitle === "string"
          ? rawTitle.trim().length > 0
          : typeof rawTitle?.text === "string" && rawTitle.text.trim().length > 0;
        const title = typeof rawTitle === "string" ? { text: rawTitle } : rawTitle;

        const layout = {
          ...fig.layout,
          paper_bgcolor: "transparent",
          plot_bgcolor: "transparent",
          font: { color: colors.font, family: "DM Mono, monospace", size: 11 },
          title: hasInternalTitle
            ? {
                ...title,
                automargin: true,
                font: {
                  color: colors.title,
                  family: "DM Mono, monospace",
                  size: 14,
                  ...(title?.font ?? {}),
                },
                pad: { t: 8, b: 12, l: 0, r: 48 },
                x: title?.x ?? 0,
                xanchor: title?.xanchor ?? "left",
                y: title?.y ?? 0.98,
                yanchor: title?.yanchor ?? "top",
              }
            : title,
          margin: {
            l: 40,
            r: 16,
            t: hasInternalTitle ? 64 : 36,
            b: 40,
            ...(fig.layout?.margin ?? {}),
          },
          xaxis: { ...fig.layout?.xaxis, gridcolor: colors.grid, linecolor: colors.grid, zerolinecolor: colors.grid },
          yaxis: { ...fig.layout?.yaxis, gridcolor: colors.grid, linecolor: colors.grid, zerolinecolor: colors.grid },
          legend: { bgcolor: "rgba(0,0,0,0)", font: { color: colors.legend } },
          showlegend: fig.data?.length > 1,
        };

        const isTable = (fig.data ?? []).some(
          (t: any) => (t.type ?? "").toLowerCase() === "table"
        );

        if (isTable) {
          Plotly.react(divRef.current!, fig.data ?? [], {
            ...fig.layout,
            margin: { l: 0, r: 0, t: 44, b: 0 },
          }, {
            responsive: false,
            displayModeBar: false,
            displaylogo: false,
          });
          return;
        }

        const isPie = (fig.data ?? []).some(
          (t: any) => ["pie", "donut"].includes((t.type ?? "").toLowerCase())
        );

        if (isPie) {
          const pieData = (fig.data ?? []).map((trace: any) => ({
            ...trace,
            textinfo: "percent",
            textfont: { color: colors.label, size: 11 },
            hovertemplate: "<b>%{label}</b><br>%{value} appointments<br>%{percent}<extra></extra>",
          }));

          Plotly.react(divRef.current!, pieData, {
            ...layout,
            showlegend: false,
            margin: { l: 16, r: 16, t: hasInternalTitle ? 64 : 36, b: 16 },
          }, {
            responsive: true,
            displayModeBar: true,
            modeBarButtonsToRemove: ["lasso2d", "select2d", "autoScale2d"],
            displaylogo: false,
          });
          return;
        }

        const dataWithLabels = (fig.data ?? []).map((trace: any) => {
          const type = (trace.type ?? "scatter").toLowerCase();
          if (type === "bar") {
            return {
              ...trace,
              texttemplate: "%{y}",
              textposition: trace.textposition ?? "outside",
              textfont: { color: colors.label, size: 11 },
            };
          }
          if (type === "scatter" || type === "scattergl") {
            const mode = trace.mode ?? "lines";
            if (mode.includes("markers")) {
              return {
                ...trace,
                mode: mode.includes("text") ? mode : `${mode}+text`,
                text: trace.y,
                textposition: "top center",
                textfont: { color: colors.label, size: 10 },
              };
            }
          }
          return trace;
        });

        Plotly.react(divRef.current!, dataWithLabels, layout, {
          responsive: true,
          displayModeBar: true,
          modeBarButtonsToRemove: ["lasso2d", "select2d", "autoScale2d"],
          displaylogo: false,
        });
      } catch (e: any) {
        if (!cancelled) setError(e.message);
      }
    });

    return () => {
      cancelled = true;
      if (divRef.current) {
        import("plotly.js-dist-min").then((Plotly) => {
          if (divRef.current) Plotly.purge(divRef.current!);
        });
      }
    };
  }, [chart.plotly_json, theme]);

  if (error) {
    return <div className="plotly-chart-error">Chart error: {error}</div>;
  }

  return (
    <div
      ref={divRef}
      className={`plotly-chart-container${chart.chart_type === "table" ? " plotly-chart-container--table" : ""}`}
    />
  );
}
